import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, Trash2, X, AlertCircle, CheckCircle, Copy, Check } from 'lucide-react';

const NETWORK_OPTIONS = [
  'BSC (BEP20)',
  'Ethereum (ERC20)',
  'TRON (TRC20)',
  'Arbitrum One',
  'SOL',
  'Polygon PoS',
  'OP Mainnet',
  'AVAXC',
];

const INPUT_CLS = 'w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all';
const LABEL_CLS = 'block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5';

const Toast = ({ msg }) => {
  if (!msg) return null;
  const ok = msg.type === 'success';
  return (
    <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm mb-5 border ${
      ok ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
    }`}>
      {ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
      {msg.message}
    </div>
  );
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <button
      onClick={handleCopy}
      className="ml-2 p-1 rounded text-gray-600 hover:text-cyan-400 transition-colors shrink-0"
      title="Copy address"
    >
      {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
    </button>
  );
};

const DepositAddresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [formData, setFormData] = useState({ network: '', address: '' });
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const { user } = useAuth();

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/deposit-addresses', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddresses(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Failed to fetch addresses.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'superadmin') fetchAddresses();
  }, [user]);

  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), 3000);
    return () => clearTimeout(t);
  }, [notification]);

  const openModal = (address = null) => {
    setCurrentAddress(address);
    setFormData(address ? { network: address.network, address: address.address } : { network: '', address: '' });
    setFormError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentAddress(null);
    setFormData({ network: '', address: '' });
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    const token = localStorage.getItem('token');
    const cfg = { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } };
    try {
      if (currentAddress) {
        await axios.put(`/api/deposit-addresses/${currentAddress._id}`, formData, cfg);
        setNotification({ type: 'success', message: 'Address updated successfully!' });
      } else {
        await axios.post('/api/deposit-addresses', formData, cfg);
        setNotification({ type: 'success', message: 'Address added successfully!' });
      }
      fetchAddresses();
      closeModal();
    } catch (err) {
      setFormError(err.response?.data?.message || 'An error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/deposit-addresses/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddresses(prev => prev.filter(a => a._id !== id));
      setNotification({ type: 'success', message: 'Address deleted.' });
    } catch {
      setNotification({ type: 'error', message: 'Failed to delete address.' });
    } finally {
      setDeleteConfirmId(null);
    }
  };

  if (loading) return (
    <div className="space-y-2 mt-4">
      {[...Array(5)].map((_, i) => <div key={i} className="h-12 rounded-xl bg-white/4 animate-pulse" />)}
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm mt-4">
      <AlertCircle size={14} /> {error}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Deposit Addresses</h1>
          <p className="text-sm text-gray-500 mt-0.5">{addresses.length} network{addresses.length !== 1 ? 's' : ''} configured</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-semibold shadow-lg shadow-cyan-500/15 hover:opacity-90 transition-all"
        >
          <Plus size={15} /> Add Address
        </button>
      </div>

      <Toast msg={notification} />

      <div className="rounded-2xl border border-white/8 overflow-hidden bg-[#0d1526]/60">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-white/6 bg-white/[0.02]">
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Network</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Address</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {addresses.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-14 text-center text-gray-600 text-sm">No deposit addresses configured yet.</td>
                </tr>
              ) : addresses.map(addr => (
                <tr key={addr._id} className="hover:bg-white/[0.015] transition-colors">
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2.5 py-1 rounded-lg bg-cyan-500/8 border border-cyan-500/15 text-cyan-300 text-xs font-semibold font-mono">
                      {addr.network}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <span className="text-xs text-gray-300 font-mono truncate max-w-[260px] lg:max-w-xs xl:max-w-md" title={addr.address}>
                        {addr.address}
                      </span>
                      <CopyButton text={addr.address} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => openModal(addr)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 text-xs hover:text-cyan-400 hover:bg-cyan-500/8 hover:border-cyan-500/20 transition-all"
                      >
                        <Edit2 size={12} /> Edit
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(addr._id)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 text-xs hover:text-red-400 hover:bg-red-500/8 hover:border-red-500/20 transition-all"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0d1526] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-white font-semibold mb-1.5">Delete Address</h3>
            <p className="text-gray-400 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 py-2.5 rounded-xl bg-red-500/12 border border-red-500/25 text-red-400 text-sm font-semibold hover:bg-red-500/20 transition-all">
                Delete
              </button>
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm font-semibold hover:bg-white/8 transition-all">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0d1526] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/8">
              <h2 className="text-base font-bold text-white">
                {currentAddress ? 'Edit Address' : 'Add New Address'}
              </h2>
              <button onClick={closeModal} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/8 transition-all">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  <AlertCircle size={13} /> {formError}
                </div>
              )}

              <div>
                <label className={LABEL_CLS}>Network</label>
                <select
                  name="network"
                  value={formData.network}
                  onChange={e => setFormData(p => ({ ...p, network: e.target.value }))}
                  className={`${INPUT_CLS} appearance-none`}
                  required
                >
                  <option value="" disabled>Select a network</option>
                  {NETWORK_OPTIONS.map(opt => (
                    <option key={opt} value={opt} className="bg-[#0d1526]">{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={LABEL_CLS}>Wallet Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
                  placeholder="0x..."
                  className={`${INPUT_CLS} font-mono`}
                  required
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-all shadow-lg shadow-cyan-500/15"
                >
                  {submitting ? 'Saving…' : currentAddress ? 'Save Changes' : 'Add Address'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm font-semibold hover:bg-white/8 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositAddresses;
